import Tag from '@atoms/Tag';
import Image from 'next/image';
import {
  type ComponentPropsWithoutRef,
  type ComponentType,
  type ReactElement,
  type RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';

import Title from '@/components/atoms/Title';
import { SectionWrap } from '@/components/store/components/SectionWrap/SectionWrap';

import {
  BookChaptersWrapper,
  ChapterDescription,
  ChapterItem,
  ChapterList,
  ChapterTitle,
  ContentColumn,
  DesktopLayout,
  ImageColumn,
  ImageWrapper,
  MobileLayout,
} from './BookChapters.styled';

interface BookChapter {
  description: string;
  image: string;
  number: number;
  title: string;
}

const chapters: BookChapter[] = [
  {
    number: 1,
    title: 'Nie spać zwiedzać',
    description:
      'Poznaj najatrakcyjniejsze miasta, punkty widokowe, ogrody i muzea. W tym rozdziale znajdziesz pigułkę wiedzy na temat każdej lokalizacji i atrakcji: ciekawostki, wskazówki i ceny wstępów. Przeczytasz, co warto zobaczyć, zrobić, zwiedzić w różnych miastach, by maksymalnie wykorzystać czas na miejscu, zgodnie z zasadą #niespaćzwiedzać…',
    image: '/static/images/teneryfa/page-1.jpg',
  },
  {
    number: 2,
    title: 'Plaża, basen, relaks',
    description:
      'Przedstawiam najpopularniejsze plaże i te mniej znane, nieoczywiste, na których nie spotkasz tłumów. Wybierz się do najlepszych charcos na kąpiel wśród skał lub snorkeling w bogatym, podwodnym świecie. A może szukasz spokojnej oazy z białym piaskiem na kąpiel z małym dzieckiem? Dowiedz się, gdzie jechać, by spędzić czas tak, jak lubisz.',
    image: '/static/images/teneryfa/page-2.jpg',
  },
  {
    number: 3,
    title: 'Dla aktywnych',
    description:
      'Góry, wąwozy i jaskinie, czyli coś dla osób preferujących aktywny wypoczynek. Wizyta w parku wodnym, nurkowanie, surfing, quady i rejs na oglądanie delfinów. W tym rozdziale znajdziesz szczegóły takich atrakcji oraz gotowe trasy trekkingowe z linkami do map. Wybierz opcje dla siebie: spokojny, popołudniowy spacer, kilkugodzinna górska wyprawa, a może lot paralotnią nad oceanem.',
    image: '/static/images/teneryfa/page-3.jpg',
  },
  {
    number: 4,
    title: 'Kuchnia, jedzenie i zwyczaje',
    description:
      'Poznaj lokalne zwyczaje, tradycyjne potrawy i miejsca, gdzie smacznie zjesz. Dowiedz się, jak wygląda śniadanie mistrzów, czym charakteryzują się tutejsze tapasy oraz gdzie możesz znaleźć wegetariańskie jedzenie. Wybierz się na targ po produkty, których nie ma w Polsce, zaplanuj wizytę w miejscowej winiarni oraz dowiedz się, jaką kawę piją Kanaryjczycy.',
    image: '/static/images/teneryfa/page-4.jpg',
  },
  {
    number: 5,
    title: 'Informacje ogólne i praktyczne',
    description:
      'Dowiedz się trochę więcej na temat Wysp Kanaryjskich i poznaj wskazówki, które pomogą Ci zaplanować wyjazd w najmniejszym detalu. Od wyboru terminu podróży i kupna lotów, poprzez nocleg w sprawdzonym miejscu, transfery, komunikację i wypożyczenie auta, aż po kwestie związane ze zdrowiem i bezpieczeństwem. Dowiedz się, na co uważać, by Twoje wakacje były udane.',
    image: '/static/images/teneryfa/page-5.jpg',
  },
];

type ChapterItemWithRefProps = ComponentPropsWithoutRef<typeof ChapterItem> &
  RefAttributes<HTMLDivElement>;
const ChapterItemWithRef =
  ChapterItem as ComponentType<ChapterItemWithRefProps>;

export const BookChapters = (): ReactElement => {
  const [activeChapter, setActiveChapter] = useState(1);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const chapterRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [top, setTop] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const height = imageWrapperRef.current?.clientHeight;
      const windowHeight = window.innerHeight;
      const top = (windowHeight - height) / 2;
      setTop(top);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleChapterClick = (index: number) => {
    const chapterElement = chapterRefs.current[index];
    if (chapterElement) {
      chapterElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <SectionWrap>
      <Title>Co znajdziesz w&nbsp;środku?</Title>
      <BookChaptersWrapper>
        <MobileLayout>
          {chapters.map(chapter => (
            <ChapterItem key={`chapterMobile-${chapter.number}`}>
              <ImageColumn>
                <Image src={chapter.image} alt={chapter.title} fill />
              </ImageColumn>
              <ContentColumn>
                <Tag title={`Część ${chapter.number}`} variant="primary" />
                <ChapterTitle>{chapter.title}</ChapterTitle>
                <ChapterDescription>{chapter.description}</ChapterDescription>
              </ContentColumn>
            </ChapterItem>
          ))}
        </MobileLayout>

        <DesktopLayout>
          <ImageColumn>
            <ImageWrapper ref={imageWrapperRef} style={{ top: top + 'px' }}>
              {chapters.map(chapter => (
                <Image
                  key={`chapterPage-${chapter.number}`}
                  src={chapter.image}
                  alt={chapter.title}
                  style={{ opacity: activeChapter === chapter.number ? 1 : 0 }}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  fill
                />
              ))}
            </ImageWrapper>
          </ImageColumn>
          <ContentColumn>
            <ChapterList>
              {chapters.map((chapter, index) => (
                <InView
                  key={`chapterItem-${chapter.number}`}
                  rootMargin="-50% 0px -50% 0px"
                  threshold={0}
                  onChange={inView => {
                    if (inView) {
                      setActiveChapter(chapter.number);
                    }
                  }}
                >
                  <ChapterItemWithRef
                    ref={el => (chapterRefs.current[index] = el)}
                    data-active={activeChapter === chapter.number}
                    isLast={chapter.number === chapters.length}
                    onClick={() => handleChapterClick(index)}
                  >
                    <Tag title={`Część ${chapter.number}`} variant="primary" />
                    <ChapterTitle>{chapter.title}</ChapterTitle>
                    <ChapterDescription>
                      {chapter.description}
                    </ChapterDescription>
                  </ChapterItemWithRef>
                </InView>
              ))}
            </ChapterList>
          </ContentColumn>
        </DesktopLayout>
      </BookChaptersWrapper>
    </SectionWrap>
  );
};
